import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Bell } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const TITLES = {
  pnl: 'Profit & Loss · تقرير الأرباح والخسائر',
  trial_balance: 'Trial Balance · ميزان المراجعة',
  balance_sheet: 'Balance Sheet · الميزانية العمومية',
};

// Shared "Notify" button — wires any accounting report into the unified
// Notification Hub (email + WhatsApp, Send Now + monthly schedule).
export const ReportNotifyButton = ({ report = 'pnl', label = 'Notify' }) => {
  const [open, setOpen] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [channel, setChannel] = useState('email');
  const [cadence, setCadence] = useState('off');
  const [lastSent, setLastSent] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/report-notifications/settings', { params: { report } });
      setRecipients((data.recipients || []).join(', '));
      setWhatsapp((data.whatsapp_recipients || []).join(', '));
      setChannel(data.channel || 'email');
      setCadence(data.cadence || 'off');
      setLastSent(data.last_sent);
    } catch { /* admin only */ }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const parseList = (v) => v.split(',').map((e) => e.trim()).filter(Boolean);

  const save = async () => {
    setBusy(true);
    try {
      await api.post('/report-notifications/settings', {
        report,
        recipients: parseList(recipients),
        whatsapp_recipients: parseList(whatsapp),
        channel, cadence,
      });
      toast.success('Delivery settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    const emails = parseList(recipients);
    const nums = parseList(whatsapp);
    if ((channel === 'email' || channel === 'both') && !emails.length) { toast.error('Add at least one email recipient'); return; }
    if (channel === 'whatsapp' && !nums.length) { toast.error('Add at least one WhatsApp number'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/report-notifications/send-now', {
        report, recipients: emails, whatsapp_recipients: nums, channel,
      });
      if (data.sent) { toast.success(`Report sent — ${(data.results || []).join(', ')}`); setLastSent(data.at); }
      else toast.error(`Failed — ${(data.results || []).join(', ')}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setBusy(false); }
  };

  const showEmail = channel === 'email' || channel === 'both';
  const showWa = channel === 'whatsapp' || channel === 'both';

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={`report-notify-open-${report}`}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-muted-foreground bg-white border border-border rounded-lg hover:bg-muted/50"
      >
        <Bell size={14} />{label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`report-notify-dialog-${report}`}>
          <DialogHeader>
            <DialogTitle>Notify · {TITLES[report] || report}</DialogTitle>
            <DialogDescription>Send this month's report now, or schedule monthly delivery via email and/or WhatsApp. Subjects are bilingual (EN/AR).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase block mb-1">Channel</label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid={`report-notify-channel-${report}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email only</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp only</SelectItem>
                  <SelectItem value="both">Email + WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {showEmail && (
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase block mb-1">Email recipients (comma-separated)</label>
                <Input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="owner@company.com, accountant@company.com" data-testid={`report-notify-emails-${report}`} />
              </div>
            )}
            {showWa && (
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase block mb-1">WhatsApp numbers (comma-separated)</label>
                <Input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+9665xxxxxxxx, +9665xxxxxxxx" data-testid={`report-notify-whatsapp-${report}`} />
                <p className="text-[10px] text-muted-foreground mt-1">Leave blank to use the numbers configured in Settings → WhatsApp.</p>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase block mb-1">Schedule</label>
              <Select value={cadence} onValueChange={setCadence}>
                <SelectTrigger data-testid={`report-notify-cadence-${report}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off (manual only)</SelectItem>
                  <SelectItem value="monthly">Monthly (1st @ 07:10 UTC)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {lastSent && <p className="text-[11px] text-muted-foreground">Last sent: {new Date(lastSent).toLocaleString()}</p>}
          </div>
          <div className="flex justify-between gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={save} disabled={busy} data-testid={`report-notify-save-${report}`}>Save schedule</Button>
            <Button size="sm" onClick={sendNow} disabled={busy} data-testid={`report-notify-send-now-${report}`}>{busy ? 'Sending…' : 'Send Now'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportNotifyButton;
